"use client"

import type * as React from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, Loader2Icon, TriangleAlertIcon, Undo2Icon } from "lucide-react"

import { useIsMobile } from "../../lib/use-mobile"

/**
 * Board S2 ("Sombre et centré"), every width: one dark pill at the bottom centre, stacked upwards, white 14px
 * semibold text, a tone icon on the left and the optional action inside the pill on the right. No close button:
 * it leaves on its own or with a swipe. A description, when a caller passes one, is a second muted line inside.
 */
const TOAST_CLASSNAMES = {
  toast:
    "!inset-x-0 !mx-auto !flex !w-fit !max-w-full items-center gap-2.5 rounded-full bg-[#17181c] py-2.5 pr-3 pl-3.5 text-white shadow-[0_12px_28px_-10px_rgba(0,0,0,0.45)] has-[[data-description]]:rounded-[22px] dark:bg-[#2c2f36]",
  content: "flex min-w-0 flex-col gap-0.5",
  title: "text-sm leading-5 font-semibold",
  description: "text-[12.5px] leading-[18px] font-medium text-white/70",
  icon: "relative flex size-[17px] shrink-0 items-center justify-center",
  success: "[&_[data-icon]]:text-[#34d399]",
  error: "[&_[data-icon]]:text-[#f87171]",
  info: "[&_[data-icon]]:text-[#7dd3fc]",
  warning: "[&_[data-icon]]:text-[#fbbf24]",
  loading: "[&_[data-icon]]:text-white/70",
  actionButton:
    "ml-1.5 inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-white/12 px-3 py-[5px] text-[13px] font-extrabold text-white transition-colors hover:bg-white/20",
  cancelButton:
    "ml-1 inline-flex shrink-0 cursor-pointer items-center rounded-full bg-transparent px-2.5 py-[5px] text-[13px] font-bold text-white/70 hover:text-white",
}

const TOAST_ICON_CLASSNAME = "size-[17px] [stroke-width:2]"

/** Undo action label for a toast ("Annuler" with the back arrow), as on board S2: `action: { label: <ToastUndoLabel>…</ToastUndoLabel> }`. */
function ToastUndoLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Undo2Icon aria-hidden className='size-[13px] [stroke-width:2]' />
      {children}
    </>
  )
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const isMobile = useIsMobile()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position='bottom-center'
      expand
      gap={10}
      offset={{ bottom: 28 }}
      // Phone: clear the bottom save pill / tab bar (both ~6.5rem with the home indicator).
      mobileOffset={{ bottom: "calc(env(safe-area-inset-bottom) + 6.5rem)", left: "1rem", right: "1rem" }}
      closeButton={false}
      className='toaster group'
      icons={{
        success: <CircleCheckIcon className={TOAST_ICON_CLASSNAME} />,
        info: <InfoIcon className={TOAST_ICON_CLASSNAME} />,
        warning: <TriangleAlertIcon className={TOAST_ICON_CLASSNAME} />,
        error: <CircleAlertIcon className={TOAST_ICON_CLASSNAME} />,
        loading: <Loader2Icon className={`${TOAST_ICON_CLASSNAME} animate-spin`} />,
      }}
      style={
        {
          // The toaster column is as wide as the longest pill may be; each pill is only as wide as its text.
          "--width": isMobile ? "100%" : "min(560px, calc(100vw - 2rem))",
        } as React.CSSProperties
      }
      toastOptions={{
        unstyled: true,
        classNames: TOAST_CLASSNAMES,
      }}
      {...props}
    />
  )
}

export { Toaster, ToastUndoLabel }
